import {
  EMPTY_SCHEDULE,
  MIN_PUBLISH_TO_UNPUBLISH_MINUTES,
  ScheduleValue,
  ScheduleValidationResult,
  validateSchedule,
} from "./scheduleValidation";

/**
 * Editing a schedule that already exists (KIWI-6576). The form is seeded with what the server holds
 * (`initial`) and the editor changes it (`current`); these helpers turn the difference into the calls
 * the backend actually has, because it will not take a second schedule for a side that already has
 * one ("already trigger is initiated"):
 *
 *   had one, still ticked, same time   -> nothing to send
 *   had one, still ticked, new time    -> reschedule
 *   had one, now unticked              -> cancel
 *   had none, now ticked               -> schedule (the ordinary publish path)
 */
export type ScheduleKind = "publish" | "unpublish";

export type ScheduleEdit = { kind: ScheduleKind; action: "reschedule" | "cancel"; time?: string };

export type ScheduleChanges = {
  /** Only the sides that are new, in the shape `planPublishSteps` takes. */
  creates: ScheduleValue;
  /** Reschedules and cancels of schedules that already existed, publish first. */
  edits: ScheduleEdit[];
};

type Side = { had: boolean; hadTime: string | null; has: boolean; time: string | null };

const sideOf = (initial: ScheduleValue, current: ScheduleValue, kind: ScheduleKind): Side =>
  kind === "publish"
    ? {
        had: initial.isSchedulePublish && Boolean(initial.schedulePublishDateTime),
        hadTime: initial.schedulePublishDateTime,
        has: current.isSchedulePublish,
        time: current.schedulePublishDateTime,
      }
    : {
        had: initial.isScheduleUnpublish && Boolean(initial.scheduleUnpublishDateTime),
        hadTime: initial.scheduleUnpublishDateTime,
        has: current.isScheduleUnpublish,
        time: current.scheduleUnpublishDateTime,
      };

const sameMoment = (a: string | null, b: string | null) =>
  Boolean(a) && Boolean(b) && new Date(a as string).getTime() === new Date(b as string).getTime();

export const planScheduleChanges = (
  initial: ScheduleValue,
  current: ScheduleValue,
): ScheduleChanges => {
  const creates: ScheduleValue = { ...EMPTY_SCHEDULE };
  const edits: ScheduleEdit[] = [];

  (["publish", "unpublish"] as const).forEach((kind) => {
    const side = sideOf(initial, current, kind);
    if (side.had && !side.has) {
      edits.push({ kind, action: "cancel" });
    } else if (side.had && side.has) {
      if (!sameMoment(side.hadTime, side.time) && side.time) {
        edits.push({ kind, action: "reschedule", time: side.time });
      }
    } else if (!side.had && side.has) {
      if (kind === "publish") {
        creates.isSchedulePublish = true;
        creates.schedulePublishDateTime = side.time;
      } else {
        creates.isScheduleUnpublish = true;
        creates.scheduleUnpublishDateTime = side.time;
      }
    }
  });

  return { creates, edits };
};

/**
 * Validates what the editor is about to apply. The lead-time rule is checked only for times they
 * actually set (new or moved), so an untouched schedule that is a few minutes from firing does not
 * block them; the publish-to-unpublish gap is checked against the schedule as it will stand, which
 * includes an unchanged side.
 */
export const validateScheduleChanges = (
  initial: ScheduleValue,
  current: ScheduleValue,
  now: Date = new Date(),
): ScheduleValidationResult => {
  const { creates, edits } = planScheduleChanges(initial, current);
  const touched: ScheduleValue = { ...creates };
  edits.forEach((edit) => {
    if (edit.action !== "reschedule") return;
    if (edit.kind === "publish") {
      touched.isSchedulePublish = true;
      touched.schedulePublishDateTime = edit.time ?? null;
    } else {
      touched.isScheduleUnpublish = true;
      touched.scheduleUnpublishDateTime = edit.time ?? null;
    }
  });

  // A ticked side with no time is a new schedule missing its time; `validateSchedule` reports that.
  const verdict = validateSchedule(touched, now);
  if (!verdict.valid) return verdict;

  if (current.isSchedulePublish && current.isScheduleUnpublish) {
    const publishAt = current.schedulePublishDateTime
      ? new Date(current.schedulePublishDateTime).getTime()
      : Number.NaN;
    const unpublishAt = current.scheduleUnpublishDateTime
      ? new Date(current.scheduleUnpublishDateTime).getTime()
      : Number.NaN;
    if (
      !Number.isNaN(publishAt) &&
      !Number.isNaN(unpublishAt) &&
      unpublishAt < publishAt + MIN_PUBLISH_TO_UNPUBLISH_MINUTES * 60_000
    ) {
      return { valid: false, field: "unpublish", messageKey: "publish_vs_unpublish_toast" };
    }
  }
  return { valid: true };
};
